import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import Combobox from '../components/Combobox';
import SignaturePad from '../components/SignaturePad';
import PrintSignatureBlock from '../components/PrintSignatureBlock';
import ExercisePicker from '../components/ExercisePicker';
import useAutosave, { AutosaveIndicator } from '../hooks/useAutosave';
import { Save, Printer, FileSignature, Dumbbell, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { safeConfirm } from '../lib/confirm';

const DEFAULT_AGREEMENT_TEXT = `PATIENT TREATMENT AGREEMENT & INFORMED CONSENT

1. SCOPE OF TREATMENT
I, the undersigned patient, voluntarily consent to receive treatment provided by the clinician(s) of this practice. The treatment may include, but is not limited to: clinical examination, manual therapy, joint mobilisation, soft-tissue techniques, electrotherapy, ultrasound, exercise prescription, postural re-education, and patient education.

2. POTENTIAL RISKS
I understand that treatment may carry risks including (but not limited to) temporary soreness, bruising, dizziness, fatigue, and — in very rare cases — adverse reactions or worsening of symptoms. The clinician has explained the most relevant risks for my specific condition and I have had the opportunity to ask questions.

3. NO GUARANTEE OF RESULTS
I acknowledge that outcomes cannot be guaranteed. Clinical results depend on many factors including my own engagement with the home programme, lifestyle changes, and the natural course of my condition.

4. ALTERNATIVES & RIGHT TO WITHDRAW
I have been informed of alternative options, including no treatment, and I may withdraw my consent at any time, for any reason, without negative consequences for any future care.

5. DATA PROCESSING (GDPR / HIPAA)
I consent to the processing of my health data strictly for the purpose of providing clinical care, generating invoices, and meeting legal record-retention obligations (15–20 years in line with EU regulations). I understand my rights to access, rectify, port, restrict and — within legal limits — erase my data. The full Privacy Notice is available on request and at the clinic reception.

6. PHOTOGRAPHY / RECORDING
Any photography, video or audio capture for clinical or educational purposes requires my separate written consent.

7. PAYMENT & CANCELLATIONS
I agree to settle invoices within the agreed terms. Cancellations made less than 24 h before the appointment may incur a fee as per the clinic's published policy.

8. EMERGENCY CONTACT & DISCLOSURE
I authorise the clinician to contact my GP / primary physician where clinically necessary, and to disclose my health information to other treating professionals on a strict need-to-know basis.

I confirm that I have read and understood the above, that I have provided accurate health-history information, and that I voluntarily consent to treatment.`;

const EMPTY = {
  patient_id: '', diagnosis: '',
  short_term_goals: '', long_term_goals: '',
  modalities: '', frequency: '', duration_weeks: 6,
  home_program: '', precautions: '',
  icd10_codes: [], icd11_codes: [], icf_codes: [],
  prescribed_exercises: [],
  agreement_text: DEFAULT_AGREEMENT_TEXT,
  patient_signature: '', signed_at: '',
};

// iter 68 — preset tick-options. Selecting one APPENDS the bullet to the
// freeform textarea so the clinician can still type custom goals.
const GOALS_PRESET = [
  'Reduce pain to ≤ 2/10 VAS',
  'Restore full pain-free ROM',
  'Improve gait symmetry',
  'Return to ADL without assistive device',
  'Improve sleep quality',
  'Reduce inflammation / swelling',
  'Improve postural alignment',
  'Restore proprioception / balance',
];
const LONG_TERM_GOALS_PRESET = [
  'Return to recreational sport',
  'Return to work without restrictions',
  'Full bilateral functional symmetry',
  'Prevent recurrence (home maintenance program)',
  'Independent living',
  'Resume training program at pre-injury level',
];
const FREQUENCY_PRESET = [
  '1×/week × 4 weeks',
  '1×/week × 6 weeks',
  '2×/week × 4 weeks',
  '2×/week × 6 weeks',
  '2×/week × 8 weeks',
  '3×/week × 4 weeks',
  '3×/week × 6 weeks',
  '1× every 2 weeks',
];

function appendBullet(curr, bullet) {
  const text = (curr || '').trim();
  if (text.split('\n').map(s => s.replace(/^[•\-]\s*/, '').trim()).includes(bullet)) {
    return curr;  // already present — don't double-add
  }
  if (!text) return `• ${bullet}`;
  return `${text}\n• ${bullet}`;
}

export default function TreatmentPlanPage({ mode = 'admin' }) {
  const { user } = useAuth();
  const { patient_id: routePid } = useParams();
  const isAdmin = user?.role === 'admin';
  const pid = mode === 'self' ? user?.user_id : routePid;

  const [form, setForm] = useState(EMPTY);
  const [patient, setPatient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [icd11Enabled, setIcd11Enabled] = useState(false);
  const [emailing, setEmailing] = useState(false);

  // Iter 69 — autosave drafts to localStorage so a phone going to sleep mid-
  // edit doesn't lose hours of planning. Cleared on successful server save.
  const { lastSavedAt, restore, clear } = useAutosave(`treatment-plan:${pid || 'new'}`, form, { enabled: !!isAdmin });

  useEffect(() => {
    if (!pid) return;
    const draft = restore();
    if (draft && typeof draft === 'object') {
      setForm(prev => ({ ...prev, ...draft, patient_id: pid }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  useEffect(() => {
    if (!pid) return;
    if (isAdmin) api.get(`/patients/${pid}`).then(r => setPatient(r.data));
    else api.get('/patients/me').then(r => setPatient(r.data));
    api.get(`/treatment-plans/${pid}`).then(r => {
      setForm(f => {
        // Iter 69 — strip null/undefined values from the server payload before
        // merging so they don't overwrite DEFAULT_AGREEMENT_TEXT and other
        // non-null EMPTY defaults. Pre-iter-69 plans have agreement_text=null
        // which would otherwise clear the consent boilerplate.
        const cleaned = Object.fromEntries(
          Object.entries(r.data || {}).filter(([, v]) => v !== null && v !== undefined)
        );
        const fromServer = { ...EMPTY, ...cleaned, patient_id: pid };
        // Always guarantee the agreement is populated (in case server omitted it).
        if (!fromServer.agreement_text) fromServer.agreement_text = DEFAULT_AGREEMENT_TEXT;
        if (f && (f.short_term_goals || f.long_term_goals || (f.prescribed_exercises||[]).length)) return f;
        return fromServer;
      });
    });
    api.get('/clinic/settings').then(r => setIcd11Enabled(!!r.data?.feature_icd11)).catch(()=>{});
  }, [pid, isAdmin]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, patient_id: pid };
      if (!isAdmin) {
        // Patient only submits signature
        if (form.patient_signature) payload.signed_at = new Date().toISOString();
      }
      await api.post('/treatment-plans', payload);
      clear();
      setSaved(true); setTimeout(()=>setSaved(false), 2000);
    } finally { setSaving(false); }
  };

  const doPrint = () => window.print();

  const sendEmail = async () => {
    if (!form.plan_id) {
      toast.error('Save the plan first before emailing it.');
      return;
    }
    if (!patient?.email) {
      toast.error('Patient has no email address on file.');
      return;
    }
    if (!await safeConfirm(`Email the treatment plan PDF to ${patient.email}?`)) return;
    setEmailing(true);
    try {
      const r = await api.post(`/treatment-plans/${pid}/send-email`);
      if (r.data.resend_configured === false) {
        toast.message(`Email service not configured — the PDF (${Math.round(r.data.attachment_size_bytes/1024)} KB) was rendered but not delivered.`);
      } else {
        toast.success(`Treatment plan sent to ${r.data.to}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Email failed.');
    } finally { setEmailing(false); }
  };

  if (!pid) return <div className="p-8">Loading…</div>;

  return (
    <div className="p-4 md:p-8 max-w-5xl print:p-4">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6 print:mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">CARE PLAN</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Evaluation & treatment plan</h1>
          {patient && (
            <p className="text-fg-muted mt-2">
              {patient.full_name} {patient.date_of_birth ? `· DOB ${patient.date_of_birth}` : ''}
            </p>
          )}
        </div>
        <div className="flex gap-2 print:hidden">
          <button onClick={doPrint} className="btn-secondary inline-flex items-center gap-2" data-testid="tp-print"><Printer className="w-4 h-4"/> Print</button>
          {/* Active controls (Save + Send) are clinician/reception only — patient view is read-only with Print. */}
          {isAdmin && (
            <>
              <button onClick={sendEmail} className="btn-secondary inline-flex items-center gap-2 disabled:opacity-50"
                      disabled={emailing || !form.plan_id || !patient?.email}
                      title={!form.plan_id ? 'Save the plan first' : !patient?.email ? 'Patient has no email' : ''}
                      data-testid="tp-send-email">
                {emailing ? <Loader2 className="w-4 h-4 animate-spin"/> : <Mail className="w-4 h-4"/>}
                {emailing ? 'Sending…' : 'Send to patient'}
              </button>
              <AutosaveIndicator lastSavedAt={lastSavedAt} onClear={clear}/>
              <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="tp-save"><Save className="w-4 h-4"/> {saving?'Saving…':'Save'}</button>
            </>
          )}
        </div>
      </div>
      {saved && <div className="mb-4 badge-success w-fit">Saved.</div>}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bento-card md:col-span-2">
          <h3 className="font-heading font-semibold mb-3">Diagnosis</h3>
          <label className="block mb-3">
            <div className="text-xs text-fg-muted mb-1">Clinical diagnosis</div>
            <textarea className="input min-h-[70px]" value={form.diagnosis || ''}
                      onChange={e=>set('diagnosis', e.target.value)}
                      disabled={!isAdmin} data-testid="tp-diagnosis"/>
          </label>
          <div className="grid md:grid-cols-2 gap-3">
            <Combobox endpoint="/lookup/icd10" value={form.icd10_codes || []} onChange={v=>set('icd10_codes', v)}
                      label="ICD-10 codes" placeholder="Search ICD-10…" testid="tp-icd10" disabled={!isAdmin}/>
            {icd11Enabled && (
              <Combobox endpoint="/lookup/icd11" value={form.icd11_codes || []} onChange={v=>set('icd11_codes', v)}
                        label="ICD-11 codes (WHO 2022)" placeholder="Search ICD-11…" testid="tp-icd11" disabled={!isAdmin}/>
            )}
            <Combobox endpoint="/lookup/icf" value={form.icf_codes || []} onChange={v=>set('icf_codes', v)}
                      label="ICF codes" placeholder="Search ICF…" testid="tp-icf" disabled={!isAdmin}/>
          </div>
        </div>

        <div className="bento-card">
          <h3 className="font-heading font-semibold mb-3">Goals &amp; approach</h3>
          <Field label="Short-term goals (2–4 weeks)"><textarea className="input min-h-[70px]" value={form.short_term_goals||''} onChange={e=>set('short_term_goals', e.target.value)} disabled={!isAdmin} data-testid="tp-st-goals"/></Field>
          {isAdmin && (
            <QuickTicks options={GOALS_PRESET} current={form.short_term_goals || ''}
                        onAppend={(v) => set('short_term_goals', appendBullet(form.short_term_goals, v))}
                        testidPrefix="tp-tick-st"/>
          )}
          <Field label="Long-term goals"><textarea className="input min-h-[70px]" value={form.long_term_goals||''} onChange={e=>set('long_term_goals', e.target.value)} disabled={!isAdmin} data-testid="tp-lt-goals"/></Field>
          {isAdmin && (
            <QuickTicks options={LONG_TERM_GOALS_PRESET} current={form.long_term_goals || ''}
                        onAppend={(v) => set('long_term_goals', appendBullet(form.long_term_goals, v))}
                        testidPrefix="tp-tick-lt"/>
          )}
          <Field label="Modalities"><input className="input" value={form.modalities||''} onChange={e=>set('modalities', e.target.value)} disabled={!isAdmin} placeholder="Manual therapy, ultrasound, TENS, exercise prescription"/></Field>
        </div>

        <div className="bento-card">
          <h3 className="font-heading font-semibold mb-3">Frequency &amp; program</h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Frequency">
              {isAdmin ? (
                <select className="input" value={form.frequency || ''} onChange={e => set('frequency', e.target.value)} data-testid="tp-frequency">
                  <option value="">— Select —</option>
                  {FREQUENCY_PRESET.map(f => <option key={f} value={f}>{f}</option>)}
                  <option value="__custom__">Other (write-in)…</option>
                </select>
              ) : (
                <input className="input" value={form.frequency||''} disabled/>
              )}
              {isAdmin && form.frequency === '__custom__' && (
                <input className="input mt-2 !text-sm"
                       placeholder="e.g. 3×/week × 8 weeks"
                       onBlur={(e) => set('frequency', e.target.value || '')}
                       data-testid="tp-frequency-custom"
                       autoFocus/>
              )}
            </Field>
            <Field label="Duration (weeks)"><input type="number" className="input" value={form.duration_weeks||''} onChange={e=>set('duration_weeks', parseInt(e.target.value)||0)} disabled={!isAdmin}/></Field>
          </div>
          <Field label="Home program"><textarea className="input" value={form.home_program||''} onChange={e=>set('home_program', e.target.value)} disabled={!isAdmin}/></Field>
          <Field label="Precautions / contraindications"><textarea className="input" value={form.precautions||''} onChange={e=>set('precautions', e.target.value)} disabled={!isAdmin}/></Field>
        </div>

        <div className="bento-card md:col-span-2">
          <h3 className="font-heading font-semibold mb-3 inline-flex items-center gap-2">
            <Dumbbell className="w-4 h-4 text-brand"/> Prescribed exercises
          </h3>
          <p className="text-xs text-fg-muted mb-3">
            Pulled from the open-source exercise library — pick manually or use the AI prescribe button.
            Each exercise is printed with sets, reps, rest, and clinician notes on the patient's program sheet.
          </p>
          <ExercisePicker
            value={form.prescribed_exercises || []}
            onChange={(next) => set('prescribed_exercises', next)}
            context="rehab"
            patientId={pid}
            readOnly={!isAdmin}
            testidPrefix="tp-ep"
          />
        </div>

        <div className="bento-card md:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <FileSignature className="w-4 h-4 text-brand"/>
            <h3 className="font-heading font-semibold">Patient treatment agreement</h3>
          </div>
          <p className="text-xs text-fg-muted mb-2">
            A complete, informed-consent agreement that meets GDPR + HIPAA + EU record-retention norms. Edit clauses to match your local jurisdiction; the patient signs below.
          </p>
          <textarea className="input min-h-[260px] text-xs font-mono leading-relaxed" value={form.agreement_text||''}
                    onChange={e=>set('agreement_text', e.target.value)}
                    disabled={!isAdmin} data-testid="tp-agreement"/>
          <div className="mt-4">
            <div className="text-xs text-fg-muted mb-2">Patient signature</div>
            <SignaturePad value={form.patient_signature}
                          onChange={dataUrl => set('patient_signature', dataUrl)}
                          readOnly={isAdmin} />
            {form.signed_at && (
              <div className="text-xs text-fg-muted mt-2">Signed {new Date(form.signed_at).toLocaleString()}</div>
            )}
          </div>
        </div>
      </div>

      <div className="hidden print:block text-xs text-fg-muted mt-6 pt-3 border-t border-soft">
        Physiogenic · Evaluation / Diagnosis / Treatment plan · Generated {new Date().toLocaleString()}
      </div>
      <PrintSignatureBlock leftLabel="Clinician / Provider" rightLabel="Patient" />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <div className="text-xs text-fg-muted mb-1">{label}</div>
      {children}
    </label>
  );
}

/**
 * QuickTicks — preset bullet chips that append to a parent textarea.
 * Iter 68. Used for short/long term goals on the Treatment Plan.
 * Already-present items render as filled chips (re-clicking is a no-op
 * because appendBullet de-dupes on text content).
 */
function QuickTicks({ options, current, onAppend, testidPrefix }) {
  const lines = (current || '').split('\n').map(s => s.replace(/^[•\-]\s*/, '').trim());
  return (
    <div className="flex flex-wrap gap-1.5 mb-3 -mt-2" data-testid={`${testidPrefix}-row`}>
      {options.map((opt) => {
        const checked = lines.includes(opt);
        const slug = opt.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 32);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onAppend(opt)}
            data-testid={`${testidPrefix}-${slug}`}
            className={`text-[11px] px-2 py-1 rounded-full border transition inline-flex items-center gap-1 ${
              checked
                ? 'bg-brand/10 border-brand text-brand'
                : 'bg-transparent border-soft text-fg-muted hover:border-brand hover:text-brand'
            }`}
          >
            <span className={`inline-block w-3 h-3 rounded-sm border ${checked ? 'bg-brand border-brand' : 'border-fg-muted/50'}`}>
              {checked && (
                <svg viewBox="0 0 16 16" className="w-full h-full text-white" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="3 8 7 12 13 4"/>
                </svg>
              )}
            </span>
            {opt}
          </button>
        );
      })}
    </div>
  );
}
